import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { User, BookOpen, FileText, Download, X, Send, Wallet, Star, Edit3, GraduationCap, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import EmployeePaymentsTab from "@/components/EmployeePaymentsTab";
import { formatDate, formatDateTime } from "@/lib/dates";

export default function EmployeePortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [pending, setPending] = useState([]);
  const [acknowledged, setAcknowledged] = useState([]);
  const [tab, setTab] = useState("policies");
  const [reading, setReading] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const [p, a, m] = await Promise.all([
        api.get("/policies/my/pending"),
        api.get("/policies/my/acknowledged"),
        api.get("/employees/me/profile"),
      ]);
      setPending(p.data || []);
      setAcknowledged(a.data || []);
      setMe(m.data);
    } catch (e) {
      toast.error("Failed to load portal");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="fade-up max-w-4xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">My portal</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3"><User className="w-7 h-7" /> Welcome, {me?.full_name || user?.name}</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">View your profile, acknowledge policies and stay compliant.</p>
        </div>
        <button
          className="btn-ghost text-sm inline-flex items-center gap-1.5 shrink-0"
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="portal-logout-button"
          aria-label="Log out"
        ><LogOut className="w-4 h-4" /> Log out</button>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { k: "policies", label: `Policies (${pending.length} pending)`, icon: BookOpen },
          { k: "payments", label: "Payments", icon: Wallet },
          { k: "appraisals", label: "My appraisals", icon: Star },
          { k: "training", label: "Training", icon: GraduationCap },
          { k: "profile",  label: "My profile", icon: User },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            className={`px-4 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-1.5 ${tab === k ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
            onClick={() => setTab(k)}
            data-testid={`portal-tab-${k}`}
          ><Icon className="w-4 h-4" /> {label}</button>
        ))}
      </div>

      {tab === "policies" && (
        <div className="space-y-5">
          <Section title={`Pending acknowledgment (${pending.length})`}>
            {pending.length === 0 && <Empty msg="No policies awaiting your acknowledgment 🎉" />}
            {pending.map((ack) => (
              <PolicyRow key={ack.id} ack={ack} onRead={() => setReading(ack)} apiBase={apiBase} />
            ))}
          </Section>
          <Section title={`Acknowledged (${acknowledged.length})`}>
            {acknowledged.length === 0 && <Empty msg="No acknowledged policies yet." />}
            {acknowledged.map((ack) => (
              <PolicyRow key={ack.id} ack={ack} onRead={() => setReading(ack)} apiBase={apiBase} done />
            ))}
          </Section>
        </div>
      )}

      {tab === "profile" && me && (
        <EmployeeProfileTab me={me} onSaved={load} />
      )}

      {tab === "payments" && <EmployeePaymentsTab viewerRole="employee" />}
      {tab === "appraisals" && <EmployeeAppraisalsTab />}
      {tab === "training" && <EmployeeTrainingTab />}

      {reading && (
        <AcknowledgeModal
          ack={reading}
          employeeName={me?.full_name}
          onClose={() => setReading(null)}
          onSaved={() => { setReading(null); load(); }}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="label-eyebrow mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Empty({ msg }) { return <div className="cc-card p-5 text-center text-sm text-[var(--cc-text-soft)]">{msg}</div>; }
function ProfRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[var(--cc-text-soft)]">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function PolicyRow({ ack, onRead, apiBase, done }) {
  return (
    <div className="cc-card p-4 flex items-center justify-between gap-3" data-testid={`portal-policy-${ack.id}`}>
      <div>
        <div className="font-medium flex items-center gap-2">
          <FileText className="w-4 h-4 opacity-50" />
          {ack.policy_name}
        </div>
        <div className="text-[11px] text-[var(--cc-text-soft)] mt-0.5">
          Version {ack.policy_version} · {done ? `Acknowledged ${ack.acknowledged_at?.slice(0,10)}` : `Due by ${ack.due_at?.slice(0,10)}`}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <a href={`${apiBase}/api/policies/${ack.policy_id}/download`} target="_blank" rel="noreferrer" className="btn-ghost text-xs"><Download className="w-3.5 h-3.5" /> Read</a>
        {!done && (
          <button className="btn-primary text-xs" onClick={onRead} data-testid={`acknowledge-${ack.id}`}>
            <Send className="w-3.5 h-3.5" /> Acknowledge
          </button>
        )}
      </div>
    </div>
  );
}

function AcknowledgeModal({ ack, employeeName, onClose, onSaved }) {
  const [signature, setSignature] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const submit = async () => {
    if (!confirmed) return toast.error("You must confirm you've read the policy");
    if (!signature.trim()) return toast.error("Type your name as signature");
    setBusy(true);
    try {
      await api.post(`/policies/${ack.policy_id}/acknowledge`, { signature: signature.trim() });
      toast.success("Acknowledged");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="acknowledge-modal">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold">Acknowledge: {ack.policy_name}</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm mb-3 leading-relaxed">
        Please read the full policy before acknowledging.
      </div>
      <a href={`${apiBase}/api/policies/${ack.policy_id}/download`} target="_blank" rel="noreferrer" className="btn-secondary text-sm inline-flex items-center gap-1.5 mb-4" data-testid="open-policy-doc-link">
        <Download className="w-3.5 h-3.5" /> Open policy document
      </a>
      <div className="cc-card p-4 mb-4 bg-[var(--cc-surface)] text-sm">
        I confirm that I have read and understand this policy and will comply with its requirements.
      </div>
      <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="ack-confirm-checkbox" />
        I acknowledge this policy
      </label>
      <div className="mb-4">
        <label className="label-eyebrow block mb-1">Signature (type your full name) *</label>
        <input className="cc-input" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder={employeeName} data-testid="ack-signature-input" />
        <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Must match your registered name on file ({employeeName})</div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy || !confirmed || !signature.trim()} data-testid="ack-submit-button">{busy ? "Acknowledging…" : "Acknowledge"}</button>
      </div>
    </Modal>
  );
}


// ===========================================================================
//  Editable profile (employee self-edit — limited whitelist)
// ===========================================================================
function EmployeeProfileTab({ me, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    preferred_name: me.preferred_name || "",
    phone_personal: me.phone_personal || "",
    personal_email: me.personal_email || "",
    emergency_contact_name: me.emergency_contact_name || "",
    emergency_contact_relationship: me.emergency_contact_relationship || "",
    emergency_contact_phone: me.emergency_contact_phone || "",
    bio: me.bio || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/employees/${me.id}`, form);
      toast.success("Profile updated");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="space-y-5">
        <div className="cc-card p-6 space-y-3" data-testid="portal-profile-readonly">
          <div className="flex justify-end">
            <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => setEditing(true)} data-testid="portal-edit-profile-button">
              <Edit3 className="w-3.5 h-3.5" /> Edit my details
            </button>
          </div>
          <ProfRow label="Name" value={me.full_name} />
          <ProfRow label="Preferred name" value={me.preferred_name || "—"} />
          <ProfRow label="Job title" value={me.job_title} />
          <ProfRow label="Department" value={me.department || "—"} />
          <ProfRow label="Work email" value={me.work_email} />
          <ProfRow label="Personal email" value={me.personal_email || "—"} />
          <ProfRow label="Phone (personal)" value={me.phone_personal || "—"} />
          <ProfRow label="Start date" value={me.start_date ? formatDate(me.start_date) : "—"} />
          <div className="pt-3 border-t border-[var(--cc-border)]">
            <div className="label-eyebrow mb-2">Emergency contact</div>
            <ProfRow label="Name" value={me.emergency_contact_name || "—"} />
            <ProfRow label="Relationship" value={me.emergency_contact_relationship || "—"} />
            <ProfRow label="Phone" value={me.emergency_contact_phone || "—"} />
          </div>
          {me.bio && (
            <div className="pt-3 border-t border-[var(--cc-border)]">
              <div className="label-eyebrow mb-1">Bio</div>
              <div className="text-sm">{me.bio}</div>
            </div>
          )}
          <div className="pt-3 border-t border-[var(--cc-border)] text-xs text-[var(--cc-text-soft)]">
            You can edit your preferred name, phone, personal email, emergency contact and bio. For other changes (job title, salary), contact HR.
          </div>
        </div>

        <BankAccountSection me={me} onSaved={onSaved} />
      </div>
    );
  }

  return (
    <div className="cc-card p-6 space-y-4" data-testid="portal-profile-edit">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-lg font-bold">Edit my details</h3>
        <div className="flex gap-2">
          <button className="btn-ghost text-sm" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /> Cancel</button>
          <button className="btn-primary text-sm" onClick={submit} disabled={busy} data-testid="portal-save-profile-button">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
      <PortalInput label="Preferred name" value={form.preferred_name} onChange={(v) => setForm({...form, preferred_name: v})} testId="portal-preferred-name" />
      <PortalInput label="Personal email" type="email" value={form.personal_email} onChange={(v) => setForm({...form, personal_email: v})} testId="portal-personal-email" />
      <PortalInput label="Phone (personal)" value={form.phone_personal} onChange={(v) => setForm({...form, phone_personal: v})} testId="portal-phone-personal" />
      <div className="pt-3 border-t border-[var(--cc-border)]">
        <div className="label-eyebrow mb-2">Emergency contact</div>
        <PortalInput label="Name" value={form.emergency_contact_name} onChange={(v) => setForm({...form, emergency_contact_name: v})} testId="portal-ec-name" />
        <PortalInput label="Relationship" value={form.emergency_contact_relationship} onChange={(v) => setForm({...form, emergency_contact_relationship: v})} testId="portal-ec-relationship" />
        <PortalInput label="Phone" value={form.emergency_contact_phone} onChange={(v) => setForm({...form, emergency_contact_phone: v})} testId="portal-ec-phone" />
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Bio (optional)</label>
        <textarea className="cc-input" rows={3} value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} data-testid="portal-bio" />
      </div>
    </div>
  );
}

function BankAccountSection({ me, onSaved }) {
  // me.bank from /me/profile is masked: {account_holder, account_number_last4, sort_code}
  const bank = me.bank || {};
  const edit = me.bank_edit || { allowed: true };
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    account_holder: bank.account_holder || "",
    account_number: "",
    sort_code: bank.sort_code || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.account_holder.trim() || !form.account_number.trim() || !form.sort_code.trim()) {
      return toast.error("Account holder, number and sort code are all required");
    }
    if (!/^\d{6,10}$/.test(form.account_number.replace(/\s|-/g, ""))) {
      return toast.error("Account number must be 6-10 digits");
    }
    if (!/^\d{2}-?\d{2}-?\d{2}$/.test(form.sort_code.replace(/\s/g, ""))) {
      return toast.error("Sort code must be in the format 12-34-56");
    }
    if (!confirm("Are you sure? You can only update your bank details once per year — HR will need to make any further changes.")) return;
    setBusy(true);
    try {
      await api.patch(`/employees/${me.id}`, {
        bank: {
          account_holder: form.account_holder.trim(),
          account_number: form.account_number.replace(/\s|-/g, ""),
          sort_code: form.sort_code.replace(/\s/g, ""),
        },
      });
      toast.success("Bank details updated");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="cc-card p-6 space-y-3" data-testid="portal-bank-readonly">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow mb-1">Bank details for payroll</div>
            <h3 className="font-display text-lg font-bold">Your payroll bank account</h3>
          </div>
          {edit.allowed ? (
            <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => setEditing(true)} data-testid="portal-edit-bank-button">
              <Edit3 className="w-3.5 h-3.5" /> Update bank details
            </button>
          ) : (
            <span className="pill pill-info text-[10px]" data-testid="portal-bank-locked">Locked — contact HR</span>
          )}
        </div>
        <ProfRow label="Account holder" value={bank.account_holder || "—"} />
        <ProfRow label="Account number" value={bank.account_number_last4 ? `••• ${bank.account_number_last4}` : "—"} />
        <ProfRow label="Sort code" value={bank.sort_code || "—"} />
        {edit.last_self_edit_at && (
          <div className="text-[11px] text-[var(--cc-text-soft)] pt-2 border-t border-[var(--cc-border)]">
            Last updated by you: {formatDateTime(edit.last_self_edit_at)}
          </div>
        )}
        {!edit.allowed && edit.reason && (
          <div className="cc-card p-3 bg-[#fdf3ec] border-l-4 border-[var(--cc-accent)] text-xs" data-testid="portal-bank-cooldown-msg">{edit.reason}</div>
        )}
      </div>
    );
  }

  return (
    <div className="cc-card p-6 space-y-4" data-testid="portal-bank-edit">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-lg font-bold">Update bank details</h3>
        <div className="flex gap-2">
          <button className="btn-ghost text-sm" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /> Cancel</button>
          <button className="btn-primary text-sm" onClick={submit} disabled={busy} data-testid="portal-save-bank-button">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
      <div className="cc-card p-3 bg-[#fdf3ec] border-l-4 border-[var(--cc-accent)] text-xs">
        <strong>One change per year.</strong> Once saved, you won't be able to edit these again for 12 months — HR will have to update them for you. Double-check before saving.
      </div>
      <PortalInput label="Account holder name *" value={form.account_holder} onChange={(v) => setForm({...form, account_holder: v})} testId="portal-bank-holder" />
      <PortalInput label="Account number * (6-10 digits)" value={form.account_number} onChange={(v) => setForm({...form, account_number: v})} testId="portal-bank-number" />
      <PortalInput label="Sort code * (12-34-56)" value={form.sort_code} onChange={(v) => setForm({...form, sort_code: v})} testId="portal-bank-sort" />
    </div>
  );
}

function PortalInput({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input className="cc-input" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

// ===========================================================================
//  Employee appraisals (self-assessment + view)
// ===========================================================================
const STAGE_LABEL = {
  scheduled: "Scheduled",
  employee_in_progress: "Awaiting your self-rating",
  manager_in_progress: "Awaiting line manager",
  hr_review: "HR review",
  signed_off: "Signed off",
  cancelled: "Cancelled",
};

function EmployeeAppraisalsTab() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/appraisals/my/employee");
      setRows(data || []);
    } catch (e) { toast.error("Failed to load appraisals"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <h3 className="font-display text-xl font-bold">My appraisals</h3>
      {rows.length === 0 && <div className="cc-card p-5 text-center text-sm text-[var(--cc-text-soft)]" data-testid="portal-empty-appraisals">No appraisals scheduled yet.</div>}
      {rows.map((a) => {
        const canSubmit = ["scheduled", "employee_in_progress"].includes(a.stage);
        return (
          <div key={a.id} className="cc-card p-4 flex items-center justify-between" data-testid={`portal-appraisal-row-${a.id}`}>
            <div>
              <div className="font-medium">{a.cycle === "annual" ? "Annual review" : "6-month probation check"}</div>
              <div className="text-xs text-[var(--cc-text-soft)]">Due {formatDate(a.due_date)} · <strong>{STAGE_LABEL[a.stage] || a.stage}</strong></div>
            </div>
            <button className="btn-primary text-sm" onClick={() => setOpenId(a.id)} data-testid={`portal-open-appraisal-${a.id}`}>
              {canSubmit ? "Complete self-rating" : "View"}
            </button>
          </div>
        );
      })}
      {openId && <EmployeeScorecardModal id={openId} onClose={() => setOpenId(null)} onSaved={() => { setOpenId(null); load(); }} />}
    </div>
  );
}

function EmployeeScorecardModal({ id, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [form, setForm] = useState({ scores: {}, strengths: "", areas_to_improve: "", goals_next_period: "", comments: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/appraisals/${id}`), api.get("/appraisals/competencies")]).then(([a, c]) => {
      setData(a.data);
      setCompetencies(c.data.competencies);
      if (a.data.employee_scorecard) setForm(a.data.employee_scorecard);
    });
  }, [id]);

  const submit = async () => {
    const missing = competencies.find((c) => !form.scores[c.key]);
    if (missing) return toast.error(`Please rate "${missing.label}"`);
    setBusy(true);
    try {
      await api.post(`/appraisals/${id}/employee-scorecard`, form);
      toast.success("Self-assessment submitted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const readOnly = !["scheduled", "employee_in_progress"].includes(data.stage);

  return (
    <Modal onClose={onClose} widthClass="max-w-2xl" testId="portal-scorecard-modal">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold">{data.cycle === "annual" ? "Annual review" : "6-month probation"}</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-[var(--cc-text-soft)] mb-4">Rate yourself 1–5 on each competency. Your line manager will then provide their rating.</p>
      <div className="space-y-3">
        {competencies.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3 cc-card p-3">
            <div className="text-sm font-medium">{c.label}</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`w-9 h-9 rounded-lg text-sm font-bold ${form.scores[c.key] === n ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
                  onClick={() => !readOnly && setForm({...form, scores: {...form.scores, [c.key]: n}})}
                  disabled={readOnly}
                  data-testid={`score-${c.key}-${n}`}
                >{n}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Strengths</label>
          <textarea className="cc-input" rows={2} value={form.strengths || ""} onChange={(e) => setForm({...form, strengths: e.target.value})} disabled={readOnly} data-testid="strengths-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Areas to improve</label>
          <textarea className="cc-input" rows={2} value={form.areas_to_improve || ""} onChange={(e) => setForm({...form, areas_to_improve: e.target.value})} disabled={readOnly} data-testid="improve-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Goals for next period</label>
          <textarea className="cc-input" rows={2} value={form.goals_next_period || ""} onChange={(e) => setForm({...form, goals_next_period: e.target.value})} disabled={readOnly} data-testid="goals-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Comments</label>
          <textarea className="cc-input" rows={2} value={form.comments || ""} onChange={(e) => setForm({...form, comments: e.target.value})} disabled={readOnly} data-testid="comments-input" />
        </div>
      </div>
      {!readOnly && (
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy} data-testid="portal-scorecard-submit">{busy ? "Submitting…" : "Submit self-assessment"}</button>
        </div>
      )}
    </Modal>
  );
}

// ===========================================================================
//  Employee training (view own records + expiry status)
// ===========================================================================
function EmployeeTrainingTab() {
  const [rows, setRows] = useState([]);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => { api.get("/training/records").then((r) => setRows(r.data || [])); }, []);

  const WIN = {
    expired:      { cls: "pill-danger",  label: "Expired" },
    expiring_30:  { cls: "pill-danger",  label: "Expires ≤ 30 days" },
    expiring_60:  { cls: "pill-warning", label: "Expires ≤ 60 days" },
    expiring_90:  { cls: "pill-warning", label: "Expires ≤ 90 days" },
    ok:           { cls: "pill-success", label: "Current" },
  };

  return (
    <div className="space-y-4">
      <h3 className="font-display text-xl font-bold">My training records</h3>
      {rows.length === 0 && <div className="cc-card p-5 text-center text-sm text-[var(--cc-text-soft)]" data-testid="portal-empty-training">No training records yet.</div>}
      {rows.map((r) => {
        const w = WIN[r.expiry_window || "ok"];
        return (
          <div key={r.id} className="cc-card p-4 flex items-center justify-between gap-3" data-testid={`portal-training-row-${r.id}`}>
            <div>
              <div className="font-medium">{r.training_type}</div>
              <div className="text-xs text-[var(--cc-text-soft)]">
                Completed {formatDate(r.completion_date)}{r.expiry_date ? ` · Expires ${formatDate(r.expiry_date)}` : ""}{r.provider ? ` · ${r.provider}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`pill ${w.cls}`}>{w.label}</span>
              {r.certificate && (
                <a className="btn-ghost text-xs inline-flex items-center gap-1" href={`${apiBase}/api/training/records/${r.id}/certificate`} target="_blank" rel="noreferrer" data-testid={`portal-cert-${r.id}`}>
                  <Download className="w-3 h-3" /> Cert
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

